import { Helmet } from 'react-helmet-async';

import { getStaticData } from '../hooks/useStaticData';

import { CDN_URL } from '@/config/cdn';

const SITE_URL = 'https://owazamartialarts.com';
const DEFAULT_OG_IMAGE = `${CDN_URL}/images/owaza-og-image.jpg`;

/**
 * SEO Component
 * Handles dynamic meta tags, Open Graph, Twitter Cards, and JSON-LD structured data
 */
const SEO = ({
  title,
  description,
  canonical,
  ogImage,
  ogType = 'website',
  jsonLd,
  noindex = false,
  // New SEO props
  keywords,
  twitterCard = 'summary_large_image',
  ogTitle,
  ogDescription,
}) => {
  const businessInfo = getStaticData('businessInfo');
  const siteName = businessInfo?.name || 'Owaza Martial Arts';

  // Build full title
  const fullTitle = title
    ? `${title} | ${siteName}`
    : `${siteName} - Jiu-Jitsu & Self-Defense in Spring Hill, FL`;

  // Default description for the business
  const defaultDescription = `Expert martial arts for all ages in Spring Hill, FL — Jiu-Jitsu, Karate, Judo, Tai Chi & self-defense. Appointment-only dojo. Call to book an intro class.`;
  const metaDescription = description || defaultDescription;

  // Image for social sharing
  const image = ogImage || DEFAULT_OG_IMAGE;

  // Canonical URL
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : SITE_URL;

  // Open Graph overrides
  const finalOgTitle = ogTitle ? `${ogTitle} | ${siteName}` : fullTitle;
  const finalOgDescription = ogDescription || metaDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Keywords (if provided) */}
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalOgTitle} />
      <meta name="twitter:description" content={finalOgDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
